/**
 * Echo's price comparison: LingoPing's monthly price next to things people buy without
 * thinking about it. One source for every place it appears (the subscribe page, the lock
 * screen, the landing page's pricing section, the trial emails), so the jokes and the numbers
 * stay the same everywhere.
 *
 * Prices are typical prices in Germany in 2026, rounded, and say "about": a cappuccino about
 * €3.50 (YouGov 2025: €3.14 average, more in cities), half a litre of beer in a bar about €5
 * (brewers' estimate), a pack of 20 cigarettes about €8 (Federal Ministry of Finance: €8.05
 * weighted average, 2026). Health statements carry a source (§5 UWG); keep them when editing.
 */
export const PRICE_COMPARISON_TITLE = 'What else does €5.99 buy?';
export const PRICE_COMPARISON_ECHO_LINE = 'I compared myself with your other habits. I am a little biased. But I also brought sources.';
export const PRICE_COMPARISON_ITEMS = [
    { id: 'cappuccino', name: 'Cappuccino', emoji: '☕', price: '≈ €3.50', per: 'one cup' },
    { id: 'beer', name: 'Beer at a bar', emoji: '🍺', price: '≈ €5', per: 'half a litre' },
    { id: 'cigarettes', name: 'Cigarettes', emoji: '🚬', price: '≈ €8', per: 'one pack' },
    { id: 'lingoping', name: 'LingoPing', emoji: '🦇', price: '€5.99', per: 'a whole month' },
];
export const PRICE_COMPARISON_ROWS = [
    {
        label: 'Lasts',
        cells: {
            cappuccino: { text: 'About 15 minutes', tone: 'bad' },
            beer: { text: 'One evening', tone: 'bad' },
            cigarettes: { text: 'A day or two', tone: 'bad' },
            lingoping: { text: '30 days', tone: 'good' },
        },
    },
    {
        label: 'Addictive',
        cells: {
            cappuccino: { text: 'Yes', tone: 'neutral' },
            beer: { text: 'Yes', tone: 'neutral' },
            cigarettes: { text: 'Very', tone: 'bad' },
            lingoping: { text: 'Yes. 5 cards every 10 minutes. Sorry.', tone: 'good' },
        },
    },
    {
        label: 'Good for your mind',
        cells: {
            cappuccino: { text: 'Wakes it up', tone: 'good' },
            beer: { text: 'Relaxes it, then forgets why', tone: 'neutral' },
            cigarettes: { text: 'No', tone: 'bad' },
            lingoping: { text: 'Yes', tone: 'good' },
        },
    },
    {
        label: 'Your memory',
        cells: {
            cappuccino: { text: 'Remembers you want another one', tone: 'neutral' },
            beer: { text: '"What was I saying?"', tone: 'bad' },
            cigarettes: { text: 'Makes it worse', tone: 'bad', source: 1 },
            lingoping: { text: 'Improves it', tone: 'good', source: 0 },
        },
    },
    {
        label: 'German you learn',
        cells: {
            cappuccino: { text: '"Einen Cappuccino, bitte."', tone: 'neutral' },
            beer: { text: 'Fluent, until the next morning', tone: 'bad' },
            cigarettes: { text: '"Hast du Feuer?"', tone: 'bad' },
            lingoping: { text: 'Thousands of words, der, die, das included', tone: 'good' },
        },
    },
    {
        label: 'Side effects',
        cells: {
            cappuccino: { text: 'Wide awake at 3 a.m.', tone: 'neutral' },
            beer: { text: 'Headache', tone: 'bad' },
            cigarettes: { text: 'Read the pack', tone: 'bad' },
            lingoping: { text: 'You start correcting der, die, das on menus', tone: 'good' },
        },
    },
    {
        label: 'Echo says',
        cells: {
            cappuccino: { text: 'Fine. Keep it.', tone: 'neutral' },
            beer: { text: 'Prost! Review first.', tone: 'neutral' },
            cigarettes: { text: 'Please don’t.', tone: 'bad' },
            lingoping: { text: 'Obviously.', tone: 'good' },
        },
    },
];
export const PRICE_COMPARISON_SOURCES = [
    {
        short: 'Spaced repetition improves how well you remember what you learn (Cepeda et al., 2006)',
        text: 'Cepeda, N. J., Pashler, H., Vul, E., Wixted, J. T., & Rohrer, D. (2006). Distributed practice ' +
            'in verbal recall tasks: A review and quantitative synthesis. Psychological Bulletin, 132(3), 354–380.',
        url: 'https://doi.org/10.1037/0033-2909.132.3.354',
    },
    {
        short: 'Smoking is linked to faster memory decline (Anstey et al., 2007)',
        text: 'Anstey, K. J., von Sanden, C., Salim, A., & O’Kearney, R. (2007). Smoking as a risk factor for ' +
            'dementia and cognitive decline: A meta-analysis of prospective studies. American Journal of ' +
            'Epidemiology, 166(4), 367–378.',
        url: 'https://doi.org/10.1093/aje/kwm116',
    },
];
export const PRICE_COMPARISON_FOOTNOTE = 'Typical prices in Germany, 2026. LingoPing: €5.99 a month including VAT, after a 14-day free trial; cancel any time.';
